import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	FieldConfiguration,
	ListBuilderCallbacks,
	TableRow,
} from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';

interface RelativeDateDisplayProps {
	field: FieldConfiguration;
	value: any;
	data: TableRow;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

type RelativeUnit = 'auto' | 'day' | 'week' | 'month' | 'year';

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A calendar date with no time ("2026-09-14", as a DATE column serializes)
 * is parsed by `new Date` as UTC midnight, which in any US time zone is the
 * evening of the day before — so "3 days ago" rendered as "4 days ago".
 * Read it as local midnight instead, the way `formatRelativeTime` does.
 */
export function parseRelativeSource(value: unknown): Date | null {
	let date: Date;
	if (value instanceof Date) {
		date = value;
	} else if (typeof value === 'string' && value !== '') {
		date = DATE_ONLY.test(value)
			? new Date(`${value}T00:00:00`)
			: new Date(value);
	} else if (typeof value === 'number') {
		date = new Date(value);
	} else {
		return null;
	}
	return isNaN(date.getTime()) ? null : date;
}

/** Whether the value is a calendar date rather than a moment in time. */
export function isDateOnlyValue(value: unknown): boolean {
	return typeof value === 'string' && DATE_ONLY.test(value);
}

function getRelativeDate(
	source: Date,
	referenceDate: Date,
	unit: RelativeUnit = 'auto',
): string {
	try {
		// Calculate difference in milliseconds
		const diffMs = source.getTime() - referenceDate.getTime();
		const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

		// Create RelativeTimeFormat instance
		const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

		// If the caller forced a specific unit, honor it directly so that e.g.
		// "Days" always renders "30 days ago" instead of auto-promoting to months.
		// numeric: 'auto' still handles same-period wording ("today", "this week").
		if (unit !== 'auto') {
			switch (unit) {
				case 'day':
					return rtf.format(diffDays, 'day');
				case 'week':
					return rtf.format(Math.round(diffDays / 7), 'week');
				case 'month':
					return rtf.format(Math.round(diffDays / 30), 'month');
				case 'year':
					return rtf.format(Math.round(diffDays / 365), 'year');
			}
		}

		// Same day (auto mode only — forced units fall through to Intl above so
		// they can render "this week" / "this month" / "this year" when diff=0).
		if (diffDays === 0) {
			return 'today';
		}

		const absDiffDays = Math.abs(diffDays);

		// Auto: select appropriate unit based on the difference
		if (absDiffDays === 1) {
			return rtf.format(diffDays, 'day');
		} else if (absDiffDays < 7) {
			return rtf.format(diffDays, 'day');
		} else if (absDiffDays < 28) {
			const weeks = Math.round(diffDays / 7);
			return rtf.format(weeks, 'week');
		} else if (absDiffDays < 365) {
			const months = Math.round(diffDays / 30);
			return rtf.format(months, 'month');
		} else {
			const years = Math.round(diffDays / 365);
			return rtf.format(years, 'year');
		}
	} catch (error) {
		// Error: Error calculating relative date:
		return '';
	}
}

export function RelativeDateDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: RelativeDateDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { styleConfig, link, dataConfig } = field;

	// Use the shared link navigation hook
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Note: For relative date display, we don't apply value mapping to the date itself
	// but we still evaluate color based on the original value
	const { colorValue } = getFieldDisplayProperties(field, value, data);

	if (!value && !dataConfig?.defaultValue) {
		return null;
	}

	// `FieldRenderer.getFieldValue` substitutes `dataConfig.defaultValue`
	// (e.g. "-" / "N/A") into `value` when the underlying record field is null.
	// That fallback string isn't parseable as a date, so detect it here and
	// render it as plain text — otherwise `getRelativeDate()` returns "" and
	// the "Default Value (when null/empty)" config appears to do nothing.
	const parsedSource = parseRelativeSource(value);

	if (parsedSource == null) {
		if (value == null || value === '') {
			return null;
		}
		const fallbackColor =
			resolveThemeColor(colorValue?.text, isDark) ?? colors.values.textPrimary;
		return <span style={{ color: fallbackColor }}>{t(String(value))}</span>;
	}

	// Determine reference date
	let referenceDate = new Date();
	const formatOptions = styleConfig?.formatOptions;

	if (formatOptions?.relativeBase === 'field' && formatOptions.relativeField) {
		const refField = formatOptions.relativeField;
		const refFieldName =
			typeof refField === 'string' ? refField : refField.fieldName;
		const refDate = parseRelativeSource(data[refFieldName]);
		if (refDate != null) {
			referenceDate = refDate;
		}
	} else if (isDateOnlyValue(value)) {
		// A calendar date is compared with today's date, not with this
		// moment: at 11pm, yesterday is one day ago, not one and a half.
		referenceDate = new Date(
			referenceDate.getFullYear(),
			referenceDate.getMonth(),
			referenceDate.getDate(),
		);
	}

	// Compute the past/future direction up front so optional `pastText` /
	// `futureText` overrides can short-circuit the timespan rendering. Sign
	// only matters here — diffDays === 0 (same day) is treated as neither past
	// nor future and always falls through to the formatted output.
	const diffDays = Math.round(
		(parsedSource.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24),
	);
	const pastOverride = formatOptions?.pastText;
	const futureOverride = formatOptions?.futureText;

	let relativeText: string;
	if (diffDays < 0 && pastOverride != null && pastOverride !== '') {
		relativeText = pastOverride;
	} else if (diffDays > 0 && futureOverride != null && futureOverride !== '') {
		relativeText = futureOverride;
	} else {
		relativeText = getRelativeDate(
			parsedSource,
			referenceDate,
			formatOptions?.relativeUnit ?? 'auto',
		);
	}

	// Determine text color based on relative position
	const resolvedTextColor = resolveThemeColor(colorValue?.text, isDark);
	let textColor = resolvedTextColor ?? colors.values.textPrimary;

	// If using color rules for overdue/upcoming status
	if (styleConfig?.colorRule?.type === 'static' && resolvedTextColor == null) {
		const diffMs = parsedSource.getTime() - referenceDate.getTime();
		const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays < 0) {
			// Past/Overdue - red
			textColor = colors.values.statusError;
		} else if (diffDays === 0) {
			// Today - amber
			textColor = colors.values.statusWarning;
		} else if (diffDays <= 7) {
			// Near future - blue
			textColor = colors.values.statusInfo;
		}
	}

	// Check if this field has linking enabled
	const isLinkEnabled = link?.enabled && (data?.['id'] || data?.['_id']);

	if (isLinkEnabled) {
		if (!isCallback && linkUrl != null) {
			return (
				<a
					href={linkUrl}
					onClick={handleLinkClick}
					className="hover:underline transition-all no-underline"
					style={{ color: colors.values.link }}
				>
					{t(relativeText)}
				</a>
			);
		}

		return (
			<button
				onClick={handleLinkClick}
				className="hover:underline transition-all text-left"
				style={{ color: colors.values.link }}
				type="button"
			>
				{t(relativeText)}
			</button>
		);
	}

	return <span style={{ color: textColor }}>{t(relativeText)}</span>;
}
